import { useEffect } from "react";

import {
  isAdminPushConfigured,
  onAdminPushMessage,
  playNotificationSound,
  preloadNotificationAudio,
  registerAdminPush,
  showForegroundNotification,
  unlockNotificationAudio,
} from "../../../services/support/adminPushService";
import useToken from "../../auth/useToken";

/**
 * Active les notifications push pour l'administrateur connecté.
 *
 * À monter une seule fois, au niveau de la navigation admin, afin que les
 * notifications arrivent quelle que soit la page consultée — et, grâce au
 * service worker, même lorsque le dashboard n'est pas au premier plan.
 */
export default function useAdminChatPush(): void {
  const { token } = useToken();

  useEffect(() => {
    if (!token || !isAdminPushConfigured()) return;

    // Précharge le fichier audio dès maintenant, pour que le tout premier
    // message n'ait pas à attendre le chargement du .wav.
    preloadNotificationAudio();

    let cancelled = false;

    registerAdminPush(token).then((status) => {
      if (cancelled) return;
      if (status !== "granted") {
        console.warn(`Notifications admin non activées (${status})`);
      }
    });

    // Déverrouillage audio : on écoute un maximum de gestes utilisateur, y
    // compris `pointerdown` et `touchstart` (mobile), car Chrome exige une
    // vraie interaction avant d'autoriser `audio.play()`.
    const unlock = () => unlockNotificationAudio();
    const unlockEvents = ["click", "keydown", "pointerdown", "touchstart"] as const;
    unlockEvents.forEach((ev) => window.addEventListener(ev, unlock, { once: true, capture: true }));

    // Dashboard au premier plan : FCM n'affiche rien de lui-même, on s'en
    // charge et on joue le son.
    const unsubscribe = onAdminPushMessage((message) => {
      playNotificationSound();
      void showForegroundNotification(message);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      unlockEvents.forEach((ev) => window.removeEventListener(ev, unlock, { capture: true }));
    };
  }, [token]);
}
